import config from 'src/config';
import axios from 'axios';
import * as tus from 'tus-js-client';
import { logger } from './logger';

const log = logger?.api?.bind(logger, 'api') || console.log;

// ---------------------------------------------------------------------------
// Log redaction helpers
// Never log the session id (sid) or anything derived from it
// (Authorization header, apiid, uploadid, filename built from apiid).
// Console output can be captured by error-reporting tools and devtools.
// ---------------------------------------------------------------------------
const REDACTED = '***';

// Replace every occurrence of `secret` inside a string value.
function maskSecret(text, secret) {
  if (typeof text !== 'string' || !secret) return text;
  return text.split(secret).join(REDACTED);
}

// Return a copy of headers that is safe to log.
// - Authorization is always fully masked.
// - Other string values (apiid, uploadid, filename) are scrubbed of the sid.
function redactHeaders(headers, sid) {
  if (!headers || typeof headers !== 'object') return headers;
  const out = {};
  Object.entries(headers).forEach(([key, value]) => {
    out[key] =
      key.toLowerCase() === 'authorization' ? 'Bearer ' + REDACTED : maskSecret(value, sid);
  });
  return out;
}

// Return a copy of request params/data without the sid.
function redactParams(params) {
  if (params && typeof params === 'object' && 'sid' in params) {
    return { ...params, sid: REDACTED };
  }
  return params;
}

// Return a copy of the axios request config that is safe to log.
// The cancelToken is dropped because it is noisy and not useful in logs.
function redactReq(req, sid) {
  if (!req) return req;
  const rest = { ...req };
  delete rest.cancelToken;
  return {
    ...rest,
    data: redactParams(rest.data),
    headers: redactHeaders(rest.headers, sid)
  };
}

// Axios errors contain error.config.headers.Authorization (and error.request),
// so never log the raw error object. Keep only the useful, non-sensitive parts.
function redactError(error) {
  return {
    message: error?.message,
    code: error?.code,
    status: error?.response?.status
  };
}

const CancelToken = axios.CancelToken;
const apiHost = config.apiHost();
const apiPath = apiHost + '/api/0/';
const sleep = 0;

// Secure random id for anonymous uploads (no sid yet).
// Use crypto.randomUUID when available (secure context only),
// otherwise fall back to crypto.getRandomValues.
function createSecureId() {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // No secure random source: fail loudly instead of using a weak id
  throw new Error('Secure random generator is not available');
}

const tempSid = createSecureId();
let apiCounter = 0;

// UI only (fake progress bar step). Not used for any id, so Math.random is fine.
function getRndInteger(min = 0, max = 1) {
  return Math.floor(Math.random() * (max - min)) + min;
}

function prepareFile(file, fieldname) {
  let appSettings = config.get('appSettings') || {};
  log('prepareFile start', file, fieldname);
  if (!file || !(file instanceof File)) return file;

  file.isUpload = true;
  file.fieldname = fieldname;
  file.uploadState = {
    status: 'uploading',
    bytesUploaded: 0,
    bytesTotal: file.size || 1,
    progress: 0,
    engine: appSettings.tus ? 'tus' : 'axios',
    startDate: Date.now()
  };
  log('prepareFile done', file, fieldname);
  return file;
}

function onFileUploadProgress(file, uploadState) {
  let _onUploadProgress =
    typeof file.onUploadProgress === 'function'
      ? [file.onUploadProgress]
      : Array.isArray(file.onUploadProgress)
        ? file.onUploadProgress
        : [];
  const oldStatus = file.uploadState?.status;
  if (oldStatus === 'aborted') return;

  file.uploadState = uploadState;
  _onUploadProgress.forEach((cb) => {
    typeof cb === 'function' && cb(file.uploadState, file);
  });
}

// Map tus HTTP errors to short codes, so the UI can show a clear reason.
// Server returns: 413 file too large, 415 file type not allowed, 429 too many uploads.
const getTusErrorCode = (error) => {
  const status = error?.originalResponse?.getStatus?.();
  if (status === 413) return 'FILE_TOO_LARGE';
  if (status === 415) return 'FILE_TYPE_NOT_ALLOWED';
  if (status === 429) return 'TOO_MANY_UPLOADS';
  if (status === 400) return 'INVALID_UPLOAD';
  return null;
};

export const tusSingleFileUpload = ({ file, apiId, index }) => {
  return new Promise((resolve, reject) => {
    const run = async () => {
      let appSettings = config.get('appSettings') || {};
      const startTime = Date.now();
      const uploadId = [apiId, index].join('_');
      const sid = config.get('sid');
      // apiId (and so uploadId) is built from the sid, so mask it before logging
      const safeUploadId = maskSecret(uploadId, sid);
      const safeApiId = maskSecret(apiId, sid);
      log('TUS single upload start', {
        file,
        uploadId: safeUploadId,
        apiId: safeApiId,
        appSettings
      });
      try {
        file.tusIndex = index;
        // let filename = file.name;
        const extname = file?.name?.split('.').pop();
        let filename = uploadId;
        if (extname) filename += '.' + extname;
        // filename starts with uploadId, so it can contain the sid too
        const safeFilename = maskSecret(filename, sid);
        const filetype = file.type || 'application/octet-stream';

        const tusParams = {
          endpoint: appSettings.endpoint,
          headers: !appSettings.tusd
            ? sid
              ? {
                  Authorization: `Bearer ${sid}`,
                  uploadid: uploadId,
                  apiid: apiId,
                  filename
                }
              : { uploadid: uploadId, apiid: apiId, filename }
            : undefined,
          metadata: {
            filename,
            filetype,
            uploadId,
            apiId
          },
          chunkSize: appSettings.chunkSize || 10 * 1024 * 1024,
          parallelUploads: appSettings.tusd ? appSettings.parallelUploads || 1 : 1,
          retryDelays: [
            0, 1000, 3000, 5000, 10000, 20000, 30000, 40000, 50000, 60000, 120000, 240000
          ],
          storeFingerprintForResuming: true,
          removeFingerprintOnSuccess: false,
          onProgress: (bytesUploaded, bytesTotal) => {
            if (file.uploadState.status === 'aborted') {
              log('onProgress - aborted, ignore');
              return;
            }
            const progress = Math.floor((bytesUploaded * 100) / bytesTotal);
            onFileUploadProgress(file, {
              ...file.uploadState,
              status: 'uploading',
              bytesUploaded,
              bytesTotal,
              progress,
              engine: 'tus',
              progressDate: Date.now()
            });
          },
          // onChunkComplete: (chunkSize, bytesUploaded, bytesTotal) => {
          //   log('onChunkComplete', { chunkSize, bytesUploaded, bytesTotal });
          // },
          onSuccess: () => {
            if (file.uploadState.status === 'aborted') {
              log('onSuccess - aborted, ignore');
              return;
            }
            const time = (Date.now() - startTime) / 1000 + 's';
            log('TUS single upload success', safeUploadId, safeFilename, time);
            onFileUploadProgress(file, {
              ...file.uploadState,
              progress: 100,
              status: 'success',
              engine: 'tus',
              endDate: Date.now()
            });
            resolve({
              error: null,
              data: { uploaded: true, file: file.name },
              time: time,
              url: uploadInstance.url
            });
          },
          onError: (error) => {
            if (file.uploadState.status === 'aborted') {
              log('onError - aborted, ignore');
              return;
            }
            const time = (Date.now() - startTime) / 1000 + 's';
            const errorCode = getTusErrorCode(error);
            onFileUploadProgress(file, {
              ...file.uploadState,
              status: 'error',
              error: error,
              errorCode,
              engine: 'tus',
              endDate: Date.now()
            });
            // Log only the message: the raw tus error can hold the original
            // request (with headers), which would leak the sid.
            logger?.warn?.(
              'TUS single upload error',
              safeUploadId,
              safeFilename,
              {
                message: error.message
              },
              time
            );
            resolve({
              error: errorCode || error.message || 'Upload failed',
              data: null,
              time: time
            });
          }
        };

        // tusParams.headers has Authorization, apiid, uploadid: redact before logging
        log('TUS params', { ...tusParams, headers: redactHeaders(tusParams.headers, sid) }, file);
        const uploadInstance = new tus.Upload(file, tusParams);
        file.uploadInstance = uploadInstance;
        file.uploadInstanceResolve = resolve;
        uploadInstance.start();
      } catch (err) {
        const time = (Date.now() - startTime) / 1000 + 's';
        logger?.warn?.('TUS file prepare error', safeUploadId, err);
        onFileUploadProgress(file, {
          ...file.uploadState,
          status: 'error',
          error: err,
          endDate: Date.now(),
          engine: 'tus'
        });
        resolve({
          error: err.message || 'Failed to prepare file',
          data: null,
          time: time
        });
      }
    };

    run().catch(reject);
  });
};

export function api(url, params, isUpload, onProgress) {
  url = url[0] === '/' ? url.replace('/', '') : url;
  let appSettings = config.get('appSettings') || {};
  let useTus = !!appSettings.tus;
  // log('start', { useTus, url, params, isUpload, onProgress });
  let startTime = Date.now();

  params = params || {};
  isUpload =
    isUpload ||
    !!Object.values(params).find((value) => {
      return Array.isArray(value) ? value[0] instanceof File : value instanceof File;
    });

  let sid = typeof params.sid == 'undefined' ? config.get('sid') : params.sid;
  let headers = {};
  let apiId = null;

  if (sid) {
    headers['Authorization'] = 'Bearer ' + sid;
    if (isUpload && useTus) {
      apiCounter += 1;
      apiId = headers['apiid'] = [sid, Date.now(), apiCounter].join('-');
    }
  } else if (isUpload && useTus) {
    apiCounter += 1;
    apiId = headers['apiid'] = [tempSid, Date.now(), apiCounter].join('-');
  }

  let fullUrl = isUpload && !useTus ? apiPath + 'upload/' + url : apiPath + 'json/' + url;
  let source = CancelToken.source();
  let files = [];
  let fileFieldName = [];
  var data = params;

  try {
    if (isUpload) {
      if (!useTus) data = new FormData();
      log('apiTus preparing files', redactParams(params));

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (value[0] instanceof File) {
              value.forEach((file) => {
                if (file instanceof File) {
                  prepareFile(file, key);
                  files.push(file);
                  fileFieldName.push(key);
                }
                if (!useTus) data.append(key, file);
              });
            } else {
              if (!useTus) data.append(key, !value ? value : JSON.stringify(value));
            }
          }
        } else {
          if (value instanceof File) {
            prepareFile(value, key);
            files.push(value);
            fileFieldName.push(key);
          }
          if (!useTus) data.append(key, value);
        }
      });

      if (!useTus) headers['Content-Type'] = 'multipart/form-data';
      log('apiTus preparing files done', files);
    }
  } catch (err) {
    logger?.error?.('apiTus preparing files error', err, redactParams(params));
    return Promise.resolve({
      error: 'Prepare uploads failed',
      data: null,
      time: (Date.now() - startTime) / 1000 + 's'
    });
  }

  let req = {
    url: fullUrl,
    method: 'POST',
    data: data,
    headers: headers,
    cancelToken: source.token
  };

  let progressInterval;
  let progressInterval2;

  if (isUpload && files.length > 0 && !useTus) {
    let isCompletedProgress = false;
    let isAlmostReached = false;
    let actualProgress = 0;

    const progressConfig = config.get('progress') || { enable: '0' };
    req.onUploadProgress = (e) => {
      const progress = Math.floor((e.loaded * 100) / e.total);
      actualProgress = progressConfig.enable === '1' ? Math.floor(progress / 2) : progress;
      if (progress === 100) {
        isCompletedProgress = true;
      }

      files.forEach((file) => {
        onFileUploadProgress(file, {
          ...file.uploadState,
          status: 'uploading',
          bytesUploaded: e.loaded,
          bytesTotal: e.total,
          progress,
          engine: 'axios',
          progressDate: Date.now()
        });
      });
    };
    if (typeof onProgress === 'function') {
      onProgress(null, actualProgress); //e is not define
      if (progressConfig.enable === '1') {
        progressInterval = setInterval(() => {
          if (isCompletedProgress && !isAlmostReached) {
            const random = getRndInteger(progressConfig.min, progressConfig.max);

            actualProgress += random;

            if (actualProgress >= 90) {
              actualProgress = 90;
              isAlmostReached = true;
            }

            onProgress({}, actualProgress);
          }
        }, progressConfig.interval);

        progressInterval2 = setInterval(() => {
          if (isAlmostReached) {
            const random = getRndInteger(progressConfig.min / 2, progressConfig.max / 2);

            actualProgress += random;

            if (actualProgress >= 99) {
              actualProgress = 99;
            }

            onProgress({}, actualProgress);
          }
        }, progressConfig.interval * 2);
      }
    }
  }

  let status = 'pending';
  var timeouted = false;
  let timeout = setTimeout(
    () => {
      if (!isUpload) {
        timeouted = true;
        source.cancel('timeout');
      }
    },
    5 * 60 * 1000
  );

  // log('init', req);
  var request = new Promise((resolve, reject) => {
    const run = async () => {
      // log('start');
      if (!useTus || !isUpload || files.length === 0) return resolve([]);
      // log('start uploads');
      for (let i = 0; i < files.length; i++) {
        if (files[i].uploadState?.status === 'uploading') {
          await tusSingleFileUpload({ file: files[i], apiId, index: i, request });
        } else {
          log('skip upload, status is not uploading', files[i], files[i].uploadState?.status);
        }
      }
      return resolve();
    };

    run().catch(reject);
  })
    .then(() => {
      // log('run', req, request);
      if (useTus && files.length > 0) {
        fileFieldName.forEach((key, i) => {
          req.data[key] = null;
          files[i].fieldname = key;
        });
        req.data.files = files
          .filter((file) => file.uploadState?.status === 'success')
          .map((file) => ({
            tusIndex: file.tusIndex,
            name: file.name,
            type: file.type,
            fieldname: file.fieldname,
            size: file.size || 0
          }));
      }
      if (useTus && files.length > 0 && req.data.files.length === 0) {
        let allCanceled = files.every((file) => file.uploadState?.status === 'aborted');
        // Show the real reason (for example FILE_TOO_LARGE) when a file has one
        const firstErrorCode = files.find((file) => file.uploadState?.errorCode)?.uploadState
          .errorCode;
        const errorText = allCanceled ? 'canceled' : firstErrorCode || 'Uploads failed';
        return Promise.resolve({
          error: errorText,
          data: {
            err: errorText
          },
          time: (Date.now() - startTime) / 1000 + 's'
        });
      }
      request.source = source;
      return axios(req);
    })
    .then(function (response) {
      if (sleep > 0) {
        return new Promise((resolve) => {
          setTimeout(function () {
            resolve(response);
          }, sleep);
        });
      } else {
        return response;
      }
    })
    .then(function (response) {
      // log('api response', req, response);
      progressInterval && clearInterval(progressInterval);
      progressInterval2 && clearInterval(progressInterval2);
      clearTimeout(timeout);
      const res = response.data;
      let result = {
        error: null,
        data: res && typeof res.data != 'undefined' ? res.data : null,
        time: (Date.now() - startTime) / 1000 + 's'
      };
      if (!res) {
        result.error = 'no data received';
      } else if (typeof res === 'string') {
        result.error = res;
      } else if (res.err) {
        result.error = res.err;
      } else if (res.error) {
        result.error = res;
      } else if (typeof res.data == 'undefined') {
        result.error = 'no data received';
      } else if (result.data && result.data.error) {
        result.error = result.data.error;
      } else if (result.data === null) {
        result.error = 'response data is null';
      }
      status = 'done';
      if (result.error) {
        log('end', url, redactParams(req.data), result);
      } else {
        log('end', url, result?.data);
      }
      files.forEach((file) => {
        if (file.uploadState.status === 'aborted') {
          log('api success - aborted, ignore');
          return;
        }
        onFileUploadProgress(file, {
          ...file.uploadState,
          status: result.error ? 'error' : 'sent',
          endDate: Date.now()
        });
      });
      return result;
    })
    .catch(function (error) {
      let result = {
        error: error,
        data: null,
        time: (Date.now() - startTime) / 1000 + 's'
      };
      progressInterval && clearInterval(progressInterval);
      progressInterval2 && clearInterval(progressInterval2);
      clearTimeout(timeout);
      if (timeouted) {
        result.error = 'timeout';
      } else if (axios.isCancel(error)) {
        status = 'canceled';
        result.error = 'canceled';
      } else {
        status = 'error';
      }
      // Do not log raw req / error: req.headers.Authorization holds the sid, and
      // axios errors carry error.config.headers too. Log sanitized copies only.
      const safeResult = {
        ...result,
        error: typeof result.error === 'string' ? result.error : redactError(error)
      };
      logger?.warn?.('api catch', redactReq(req, sid), url, safeResult, redactError(error));
      files.forEach((file) => {
        if (file.uploadState.status === 'aborted') {
          log('api catch - aborted, ignore');
          return;
        }
        onFileUploadProgress(file, {
          ...file.uploadState,
          status: status === 'canceled' ? 'aborted' : 'error',
          endDate: Date.now()
        });
      });
      return result;
    });

  request.cancel = function () {
    log('cancel', url);
    if (request.source) request.source.cancel();
  };

  request.getStatus = function () {
    return status;
  };

  if (isUpload && files.length > 0) {
    files.forEach((file) => {
      file.cancelUpload = () => {
        try {
          log('cancelUpload', file);
          if (file.uploadState?.status !== 'uploading') {
            log('cancelUpload skipped, status is not uploading', file.uploadState?.status);
            return;
          }
          onFileUploadProgress(file, {
            ...file.uploadState,
            status: 'aborted',
            error: 'aborted by user',
            endDate: Date.now()
          });
          if (useTus) {
            if (file.uploadInstance) {
              log('cancelUpload uploadInstance.abort');
              file.uploadInstance.abort();
              if (file.uploadInstanceResolve) {
                log('cancelUpload uploadInstanceResolve');
                file.uploadInstanceResolve({
                  error: 'Upload aborted by user',
                  data: null,
                  time: (Date.now() - startTime) / 1000 + 's'
                });
                delete file.uploadInstanceResolve;
              }
            } else {
              log('cancelUpload no uploadInstance');
            }
          } else {
            log('cancelUpload request.cancel()');
            request.cancel();
          }
        } catch (err) {
          log('cancelUpload error', file, err);
        }
      };
    });
  }

  return request;
}
